import { UI } from '../config'
import { BRAND_SPECIFIC_TAG } from '../containers/BrandSpecificInformation/BrandSpecificInformation'
import { getArticlesWithTags, getPublicationsForVehicle } from '../extras/utils'
import {
  collectDitaHtmlKeys,
  collectOwnerManualPdfUrls,
  collectPdfUrls,
} from './offlineDownloader'
import {
  deleteHtmlAssets,
  deleteOfflineData,
  getDownloadMeta,
  removeDownloadType,
  uncachePdfUrl,
  updateDownloadItems,
} from './offlineStorage'
import { COMPONENT_MANUAL_CATEGORY, COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE } from './offlineComponentManualPdf'

/**
 * Resolves cached assets for a browse menu category title
 *
 * @param {string} categoryTitle menu item title
 * @param {object} contents Redux contents slice
 * @param {object} ownersManualPdfs Immutable list of owner's manual PDFs
 * @returns {{ htmlKeys: Array<string>, pdfUrls: Array<string> }}
 */
export function getAssetsForCategory(categoryTitle, contents, ownersManualPdfs) {
  const { topics, publications, foldersIndex } = contents || {}
  const pubs = publications ? getPublicationsForVehicle(publications.toJS()) : []

  if (categoryTitle === UI.OWNERS_MANUAL) {
    return {
      htmlKeys: collectDitaHtmlKeys(topics, foldersIndex),
      pdfUrls: [],
    }
  }

  if (categoryTitle === UI.PRINTABLE_OWNERS_MANUAL) {
    return {
      htmlKeys: [],
      pdfUrls: collectOwnerManualPdfUrls(ownersManualPdfs),
    }
  }

  if (categoryTitle === 'Component Manual') {
    return {
      htmlKeys: [],
      pdfUrls: publications ? collectPdfUrls(publications) : [],
    }
  }

  if (categoryTitle === BRAND_SPECIFIC_TAG) {
    const articles = getArticlesWithTags(pubs).filter(
      article => article.tags && article.tags.includes(BRAND_SPECIFIC_TAG)
    )
    return {
      htmlKeys: getArticleHtmlKeysForArticles(articles, topics),
      pdfUrls: [],
    }
  }

  const articles = getArticlesWithTags(pubs).filter(
    article => article.tags && article.tags.includes(categoryTitle)
  )

  return {
    htmlKeys: getArticleHtmlKeysForArticles(articles, topics),
    pdfUrls: [],
  }
}

/**
 * @param {Array} articles article publications
 * @param {object} topics Immutable topics list
 * @returns {Array<string>}
 */
function getArticleHtmlKeysForArticles(articles, topics) {
  const keys = []
  articles.forEach(article => {
    if (!article.contents || !article.contents.length || !topics) return
    const topicId = article.contents[0].id
    const topic = topics.find(t => t.get('id') === topicId)
    if (topic) {
      const objectKey = topic.getIn(['bodyHtml', 'objectKey'])
      if (objectKey) keys.push(objectKey)
    }
  })
  return keys
}

/**
 * Removes a single downloaded category from offline storage
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} categoryTitle browse menu item title
 * @param {object} contents Redux contents slice
 * @param {object} ownersManualPdfs Immutable list of owner's manual PDFs
 * @returns {Promise<void>}
 */
export async function removeDownloadedCategory(vehicleId, categoryTitle, contents, ownersManualPdfs) {
  const meta = await getDownloadMeta(vehicleId)
  if (!meta || !meta.downloads) return

  if (categoryTitle === COMPONENT_MANUAL_CATEGORY && meta.downloads[COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE]) {
    const individual = meta.downloads[COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE]
    await Promise.all((individual.pdfUrls || []).map(url => uncachePdfUrl(url)))
    await removeDownloadType(vehicleId, COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE)
  }

  const downloadType = meta.downloads['all-content']
    ? 'all-content'
    : meta.downloads['owners-manual']
      ? 'owners-manual'
      : null

  if (!downloadType) return

  const download = meta.downloads[downloadType]
  const items = download.items || []
  if (!items.includes(categoryTitle)) return

  const { htmlKeys, pdfUrls } = getAssetsForCategory(categoryTitle, contents, ownersManualPdfs)
  await deleteHtmlAssets(htmlKeys)
  await Promise.all(pdfUrls.map(url => uncachePdfUrl(url)))

  const remainingItems = items.filter(item => item !== categoryTitle)
  const remainingObjectKeys = (download.objectKeys || []).filter(key => !htmlKeys.includes(key))
  const remainingPdfUrls = (download.pdfUrls || []).filter(url => !pdfUrls.includes(url))

  await updateDownloadItems(vehicleId, downloadType, remainingItems, {
    objectKeys: remainingObjectKeys,
    pdfUrls: remainingPdfUrls,
  })
}

/**
 * Removes all offline content for a vehicle
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<void>}
 */
export async function removeAllOfflineContent(vehicleId) {
  await deleteOfflineData(vehicleId)
}
